import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type AxiosResponse } from "axios";
import { useAtomValue } from "jotai";
import {
  type OrderItemAddDeliveryLabelCommand,
  OrderItemApi,
  type OrderItemCreateCommand,
  type OrderItemDeliveryLabelPatchCommand,
  type OrderItemDetailsReadDtoContainer,
  type OrderItemMarkOrderItemAsDeliveredCommand,
  OrderItemProofApi,
  type OrderItemUpdateCommand,
  type OrderItemUpdateDeliveryNotesCommand,
} from "@/config/apiClient";
import { type ApiErrorResponse } from "@/config/apiClient.types";
import { createApiClient } from "@/config/createApiClient";
import { currentWorkspaceAtom } from "@/store";
import { useSuggestedFixRedirect } from "../useSuggestedFixRedirect";

const apiClient = createApiClient(OrderItemApi);
const orderItemProofApiClient = createApiClient(OrderItemProofApi);

export const useOrderItems = () => {
  const currentWorkspace = useAtomValue(currentWorkspaceAtom);

  const queryClient = useQueryClient();

  const useAddOrderItemProof = () =>
    useMutation({
      mutationKey: ["addOrderItemProof"],
      mutationFn: (params: {
        orderId: string;
        orderItemId: string;
        orderItemProofAddCommand: Parameters<
          typeof orderItemProofApiClient.orderItemProofsControllerAddOrderItemProof
        >[0]["orderItemProofAddCommand"];
      }) =>
        orderItemProofApiClient.orderItemProofsControllerAddOrderItemProof({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemProofAddCommand: params.orderItemProofAddCommand,
        }),
      onSuccess: async (_, params) => {
        await Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
        ]);
      },
    });

  const useUpdateOrderItemsDeliveryNote = () =>
    useMutation<
      Awaited<
        ReturnType<
          typeof apiClient.orderItemsControllerUpdateOrderItemsDeliveryNotes
        >
      >,
      Error,
      {
        orderId: string;
        orderItemId: string;
        orderItemUpdateDeliveryNotesCommand: OrderItemUpdateDeliveryNotesCommand;
      }
    >({
      mutationKey: ["orderItemUpdateDeliveryNotes"],
      mutationFn: (params) =>
        apiClient.orderItemsControllerUpdateOrderItemsDeliveryNotes({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemUpdateDeliveryNotesCommand:
            params.orderItemUpdateDeliveryNotesCommand,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["documents"] }),
        ]),
    });

  const useGetOrderItem = (params: {
    orderId: string;
    orderItemId: string;
    enabled?: boolean;
  }) => {
    const query = useQuery<
      AxiosResponse<OrderItemDetailsReadDtoContainer>,
      ApiErrorResponse
    >({
      queryKey: [
        "orderItem",
        params.orderId,
        params.orderItemId,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      enabled:
        !!currentWorkspace?.accountId &&
        !!currentWorkspace?.organizationId &&
        !!currentWorkspace?.id &&
        !!params.orderId &&
        !!params.orderItemId &&
        (params.enabled === undefined || params.enabled),
      queryFn: () =>
        apiClient.orderItemsControllerGetOrderItem({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
        }),
    });

    const { is404Error } = useSuggestedFixRedirect(query);

    return { ...query, is404Error };
  };

  const useCreateOrderItem = () =>
    useMutation<
      Awaited<ReturnType<typeof apiClient.orderItemsControllerCreateOrderItem>>,
      Error,
      { orderId?: string; orderItemCreateCommand: OrderItemCreateCommand }
    >({
      mutationKey: ["createOrderItem"],
      mutationFn: (params) => {
        return params.orderId
          ? apiClient.orderItemsControllerCreateOrderOrderItem({
              accountId: currentWorkspace?.accountId,
              organizationId: currentWorkspace?.organizationId,
              workspaceId: currentWorkspace?.id,
              orderId: params.orderId,
              orderItemCreateCommand: params.orderItemCreateCommand,
            })
          : apiClient.orderItemsControllerCreateOrderItem({
              accountId: currentWorkspace?.accountId,
              organizationId: currentWorkspace?.organizationId,
              workspaceId: currentWorkspace?.id,
              orderItemCreateCommand: params.orderItemCreateCommand,
            });
      },
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          queryClient.invalidateQueries({ queryKey: ["addresses"] }),
        ]),
    });

  const useUpdateOrderItem = () =>
    useMutation<
      Awaited<ReturnType<typeof apiClient.orderItemsControllerUpdateOrderItem>>,
      Error,
      {
        orderId: string;
        orderItemId: string;
        orderItemUpdateCommand: OrderItemUpdateCommand;
        limitedRefetch?: boolean;
      }
    >({
      mutationKey: ["updateOrderItem"],
      mutationFn: (params) =>
        apiClient.orderItemsControllerUpdateOrderItem({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemUpdateCommand: params.orderItemUpdateCommand,
          orderItemId: params.orderItemId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
          !params.limitedRefetch &&
            queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          !params.limitedRefetch &&
            queryClient.invalidateQueries({ queryKey: ["addresses"] }),
        ]),
    });

  const useDeleteOrderItem = () =>
    useMutation<
      Awaited<ReturnType<typeof apiClient.orderItemsControllerDeleteOrderItem>>,
      Error,
      { orderItemId: string; orderId: string }
    >({
      mutationKey: ["deleteOrderItem"],
      mutationFn: (params) =>
        apiClient.orderItemsControllerDeleteOrderItem({
          orderItemId: params.orderItemId,
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  const useCancelOrderItem = () =>
    useMutation<
      Awaited<ReturnType<typeof apiClient.orderItemsControllerCancelOrderItem>>,
      Error,
      { orderItemId: string; orderId: string }
    >({
      mutationKey: ["cancelOrderItem"],
      mutationFn: (params) =>
        apiClient.orderItemsControllerCancelOrderItem({
          orderItemId: params.orderItemId,
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  const useGetOrderItemLogs = (params: {
    orderId: string;
    orderItemId: string;
  }) =>
    useQuery({
      queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
      queryFn: () => {
        return apiClient.orderItemsControllerGetOrderItemLogs({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
        });
      },
    });

  const useMarkOrderItemAsDelivered = () =>
    useMutation<
      Awaited<
        ReturnType<
          typeof apiClient.orderItemsControllerMarkOrderItemAsDelivered
        >
      >,
      Error,
      {
        orderItemId: string;
        orderId: string;
        orderItemMarkAsDeliveredCommand: OrderItemMarkOrderItemAsDeliveredCommand;
      }
    >({
      mutationKey: ["markOrderItemAsDelivered"],
      mutationFn: (params) =>
        apiClient.orderItemsControllerMarkOrderItemAsDelivered({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemMarkOrderItemAsDeliveredCommand:
            params.orderItemMarkAsDeliveredCommand,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  const useDownloadAllOrderItemHDFiles = ({
    orderId,
    orderItemId,
  }: {
    orderId: string;
    orderItemId: string;
  }) => {
    return useQuery({
      queryKey: [
        "downloadAllOrderItemHDFiles",
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
        orderId,
        orderItemId,
      ],
      queryFn: () =>
        apiClient.orderItemsControllerDownloadAllDesigns(
          {
            orderItemId,
            accountId: currentWorkspace?.accountId,
            organizationId: currentWorkspace?.organizationId,
            workspaceId: currentWorkspace?.id,
            orderId,
          },
          {
            responseType: "blob",
          }
        ),
      enabled: false,
    });
  };

  const useMarkDraftOrderItemAsInProgress = () =>
    useMutation<
      Awaited<
        ReturnType<
          typeof apiClient.orderItemsControllerMarkDraftOrderItemAsInProgress
        >
      >,
      Error,
      {
        orderItemId: string;
        orderId: string;
      }
    >({
      mutationKey: ["markDraftOrderItemAsInProgress"],
      mutationFn: (params) =>
        apiClient.orderItemsControllerMarkDraftOrderItemAsInProgress({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.invalidateQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  const useGetAffectedOrderItemsByQuoteCancellation = (params: {
    isEnabled: boolean;
    orderItemId: string;
    orderId: string;
  }) =>
    useQuery({
      enabled: params.isEnabled,
      queryKey: [
        "affectedOrderItemsByQuoteCancellation",
        params.orderItemId,
        params.orderId,
        currentWorkspace?.accountId,
        currentWorkspace?.organizationId,
        currentWorkspace?.id,
      ],
      queryFn: () =>
        apiClient.orderItemsControllerGetAffectedOrderItemsByQuoteCancellation({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderItemId: params.orderItemId,
          orderId: params.orderId,
        }),
    });

  const useAddOrderItemDeliveryLabel = () =>
    useMutation<
      Awaited<
        ReturnType<
          typeof apiClient.orderItemDeliveryLabelsControllerAddOrderItemDeliveryLabel
        >
      >,
      Error,
      {
        orderItemId: string;
        orderId: string;
        orderItemAddDeliveryLabelCommand: OrderItemAddDeliveryLabelCommand;
      }
    >({
      mutationKey: ["addDeliveryLabel"],
      mutationFn: (params) =>
        apiClient.orderItemDeliveryLabelsControllerAddOrderItemDeliveryLabel({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemAddDeliveryLabelCommand:
            params.orderItemAddDeliveryLabelCommand,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  const useValidateOrderItemDeliveryLabel = () =>
    useMutation<
      Awaited<
        ReturnType<
          typeof apiClient.orderItemDeliveryLabelsControllerPatchOrderItemDeliveryLabel
        >
      >,
      Error,
      {
        orderItemId: string;
        orderId: string;
        orderItemDeliveryLabelPatchCommand: OrderItemDeliveryLabelPatchCommand;
      }
    >({
      mutationKey: ["validateDeliveryLabel"],
      mutationFn: (params) =>
        apiClient.orderItemDeliveryLabelsControllerPatchOrderItemDeliveryLabel({
          accountId: currentWorkspace?.accountId,
          organizationId: currentWorkspace?.organizationId,
          workspaceId: currentWorkspace?.id,
          orderId: params.orderId,
          orderItemId: params.orderItemId,
          orderItemDeliveryLabelPatchCommand:
            params.orderItemDeliveryLabelPatchCommand,
        }),
      onSuccess: (_, params) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: ["orders"] }),
          queryClient.refetchQueries({ queryKey: ["order", params.orderId] }),
          queryClient.refetchQueries({
            queryKey: ["orderItemLogs", params.orderId, params.orderItemId],
          }),
          queryClient.refetchQueries({
            queryKey: ["orderItem", params.orderId, params.orderItemId],
          }),
          queryClient.invalidateQueries({ queryKey: ["worklist"] }),
          queryClient.invalidateQueries({ queryKey: ["activeOrders"] }),
        ]),
    });

  return {
    useAddOrderItemProof,
    useUpdateOrderItemsDeliveryNote,
    useUpdateOrderItem,
    useCancelOrderItem,
    useCreateOrderItem,
    useGetOrderItemLogs,
    useGetOrderItem,
    useMarkOrderItemAsDelivered,
    useMarkDraftOrderItemAsInProgress,
    useDownloadAllOrderItemHDFiles,
    useDeleteOrderItem,
    useGetAffectedOrderItemsByQuoteCancellation,
    useAddOrderItemDeliveryLabel,
    useValidateOrderItemDeliveryLabel,
  };
};
